import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Users, Search, Trash2, RefreshCw, Globe, BookOpen, 
  FileText, Clock, Calendar, Flame, Shield, ChevronLeft, ChevronRight, Eye
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Import centralized language utilities
import { getCountryCode } from '../utils/languageUtils';

const FlagImage = ({ countryCode, className = "w-5 h-4" }) => {
  if (!countryCode) {
    return <Globe className={`${className} text-muted-foreground`} />;
  }
  return (
    <img 
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      alt={countryCode}
      className={`${className} object-cover rounded`}
      loading="lazy"
    />
  );
};

export default function AdminUsers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null, keepPublicTexts: false });
  const [deleting, setDeleting] = useState(false);
  const [creditsDialog, setCreditsDialog] = useState({ open: false, user: null, amount: '', type: 'add' });
  const [updatingCredits, setUpdatingCredits] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch users
  const fetchUsers = async (search = '', offset = 0) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', '50');
      params.append('offset', offset.toString());

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users?${params.toString()}`,
        { withCredentials: true }
      );
      
      setUsers(response.data.users);
      setPagination({
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset
      });
    } catch (err) {
      console.error('Error fetching users:', err);
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch summary stats
  const fetchSummary = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users/stats/summary`,
        { withCredentials: true }
      );
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      fetchUsers();
      fetchSummary();
    }
  }, [user]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchQuery, 0);
  };

  // Handle delete
  const handleDeleteClick = (userToDelete) => {
    setDeleteDialog({ open: true, user: userToDelete, keepPublicTexts: false });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.user) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/users/${deleteDialog.user.user_id}?keep_public_texts=${deleteDialog.keepPublicTexts}`,
        { withCredentials: true }
      );
      const message = deleteDialog.keepPublicTexts 
        ? `User ${deleteDialog.user.email} deleted (public texts preserved)`
        : `User ${deleteDialog.user.email} deleted successfully`;
      toast.success(message);
      setDeleteDialog({ open: false, user: null, keepPublicTexts: false });
      fetchUsers(searchQuery, pagination.offset);
      fetchSummary();
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  // Handle tier change
  const handleTierChange = async (userId, newTier) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/users/${userId}/tier?tier=${newTier}`,
        {},
        { withCredentials: true }
      );
      // Update local state
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, tier: newTier } : u
      ));
      toast.success(`User tier updated to Tier ${newTier}`);
    } catch (err) {
      console.error('Error updating tier:', err);
      toast.error(err.response?.data?.detail || 'Failed to update tier');
    }
  };

  // Handle credits adjustment
  const handleCreditsSubmit = async () => {
    if (!creditsDialog.user || !creditsDialog.amount) return;
    
    const amount = parseInt(creditsDialog.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid positive number');
      return;
    }
    
    const finalAmount = creditsDialog.type === 'subtract' ? -amount : amount;
    
    setUpdatingCredits(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/credits/admin/add`,
        {
          user_email: creditsDialog.user.email,
          amount: finalAmount
        },
        { withCredentials: true }
      );
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.user_id === creditsDialog.user.user_id 
          ? { ...u, credits: (u.credits || 0) + finalAmount } 
          : u
      ));
      
      toast.success(`${creditsDialog.type === 'subtract' ? 'Deducted' : 'Added'} ${amount} credits ${creditsDialog.type === 'subtract' ? 'from' : 'to'} ${creditsDialog.user.email}`);
      setCreditsDialog({ open: false, user: null, amount: '', type: 'add' });
    } catch (err) {
      console.error('Error updating credits:', err);
      toast.error(err.response?.data?.detail || 'Failed to update credits');
    } finally {
      setUpdatingCredits(false);
    }
  };

  // Pagination
  const handlePrevPage = () => {
    const newOffset = Math.max(0, pagination.offset - pagination.limit);
    fetchUsers(searchQuery, newOffset);
  };

  const handleNextPage = () => {
    const newOffset = pagination.offset + pagination.limit;
    if (newOffset < pagination.total) {
      fetchUsers(searchQuery, newOffset);
    }
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatLastSeen = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return formatDate(dateStr);
  };

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <Shield className="h-6 w-6 text-amber-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">User Management</h1>
            <p className="text-sm text-muted-foreground">Manage all users in the system</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchUsers(searchQuery, pagination.offset); fetchSummary(); }}
          data-testid="refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total Users</span>
              </div>
              <p className="text-2xl font-bold mt-1">{summary.total_users}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-green-500" />
                <span className="text-sm text-muted-foreground">New (7d)</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-green-500">+{summary.new_users_7d}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Flame className="h-4 w-4 text-orange-500" />
                <span className="text-sm text-muted-foreground">Active (7d)</span>
              </div>
              <p className="text-2xl font-bold mt-1">{summary.active_users_7d}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total Texts</span>
              </div>
              <p className="text-2xl font-bold mt-1">{summary.total_texts}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Words Tracked</span>
              </div>
              <p className="text-2xl font-bold mt-1">{summary.total_words_tracked?.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email or name..."
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" data-testid="search-btn">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Users ({pagination.total})</CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Page {currentPage} of {totalPages || 1}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handlePrevPage} 
                disabled={pagination.offset === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleNextPage} 
                disabled={pagination.offset + pagination.limit >= pagination.total}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No users found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead className="text-center">Credits</TableHead>
                    <TableHead>Languages</TableHead>
                    <TableHead className="text-center">Words</TableHead>
                    <TableHead className="text-center">Texts</TableHead>
                    <TableHead className="text-center">Hours</TableHead>
                    <TableHead className="text-center">Days</TableHead>
                    <TableHead className="text-center">Streak</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Last Seen</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{u.name || 'Unnamed'}</span>
                            {u.is_admin && (
                              <Badge variant="outline" className="text-amber-500 border-amber-500/50">
                                Admin
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{u.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <RadioGroup
                          value={String(u.tier || 1)}
                          onValueChange={(value) => handleTierChange(u.user_id, parseInt(value))}
                          className="flex gap-3"
                          data-testid={`tier-radio-${u.user_id}`}
                        >
                          {[1, 2, 3].map((tier) => (
                            <div key={tier} className="flex items-center space-x-1">
                              <RadioGroupItem 
                                value={String(tier)} 
                                id={`tier-${u.user_id}-${tier}`}
                                className="h-3.5 w-3.5"
                              />
                              <Label 
                                htmlFor={`tier-${u.user_id}-${tier}`}
                                className={`text-xs cursor-pointer ${
                                  (u.tier || 1) === tier 
                                    ? tier === 3 ? 'text-purple-500 font-medium' 
                                      : tier === 2 ? 'text-blue-500 font-medium' 
                                      : 'font-medium'
                                    : 'text-muted-foreground'
                                }`}
                              >
                                {tier}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => setCreditsDialog({ open: true, user: u, amount: '', type: 'add' })}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium hover:bg-muted transition-colors cursor-pointer"
                          data-testid={`credits-btn-${u.user_id}`}
                        >
                          <span className={`${(u.credits || 0) > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {(u.credits || 0).toLocaleString()}
                          </span>
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {u.languages?.length > 0 ? (
                            u.languages.slice(0, 3).map((lang, idx) => (
                              <FlagImage 
                                key={idx} 
                                countryCode={getCountryCode(lang)} 
                                className="w-5 h-4"
                              />
                            ))
                          ) : (
                            <span className="text-muted-foreground text-xs">None</span>
                          )}
                          {u.languages?.length > 3 && (
                            <span className="text-xs text-muted-foreground">+{u.languages.length - 3}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <BookOpen className="h-3 w-3 text-muted-foreground" />
                          <span>{u.word_count?.toLocaleString() || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <FileText className="h-3 w-3 text-muted-foreground" />
                          <span>{u.text_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span>{u.hours_of_use || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span>{u.days_active || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Flame className={`h-3 w-3 ${u.current_streak > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                          <span className={u.current_streak > 0 ? 'text-orange-500 font-medium' : ''}>
                            {u.current_streak || 0}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(u.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Eye className={`h-3 w-3 ${u.last_seen ? 'text-green-500' : 'text-muted-foreground'}`} />
                          <span className={`text-sm ${u.last_seen ? '' : 'text-muted-foreground'}`}>
                            {formatLastSeen(u.last_seen)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(u)}
                          disabled={u.user_id === user.user_id}
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          data-testid={`delete-user-${u.user_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  Are you sure you want to delete <strong>{deleteDialog.user?.email}</strong>?
                </p>
                <br />
                <p>This will permanently remove:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>User account and settings</li>
                  <li>All {deleteDialog.user?.text_count || 0} imported texts</li>
                  <li>All {deleteDialog.user?.word_count || 0} vocabulary entries</li>
                  <li>Learning history and statistics</li>
                </ul>
                
                {/* Option to keep public texts */}
                <div className="flex items-center space-x-2 mt-4 p-3 bg-muted rounded-lg">
                  <Checkbox 
                    id="keepPublicTexts" 
                    checked={deleteDialog.keepPublicTexts}
                    onCheckedChange={(checked) => setDeleteDialog(prev => ({ ...prev, keepPublicTexts: checked }))}
                  />
                  <Label htmlFor="keepPublicTexts" className="text-sm cursor-pointer">
                    Keep public texts in library (attribute to "Deleted User")
                  </Label>
                </div>
                
                <br />
                <strong className="text-red-500">This action cannot be undone.</strong>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Credits Adjustment Dialog */}
      <AlertDialog open={creditsDialog.open} onOpenChange={(open) => !updatingCredits && setCreditsDialog({ ...creditsDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Adjust Credits</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-4">
                  Manage credits for <strong>{creditsDialog.user?.email}</strong>
                </p>
                <p className="text-sm mb-4">
                  Current balance: <span className="font-semibold text-foreground">{(creditsDialog.user?.credits || 0).toLocaleString()} credits</span>
                </p>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Button
                      variant={creditsDialog.type === 'add' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCreditsDialog(prev => ({ ...prev, type: 'add' }))}
                      className={creditsDialog.type === 'add' ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      Add Credits
                    </Button>
                    <Button
                      variant={creditsDialog.type === 'subtract' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCreditsDialog(prev => ({ ...prev, type: 'subtract' }))}
                      className={creditsDialog.type === 'subtract' ? 'bg-red-600 hover:bg-red-700' : ''}
                    >
                      Subtract Credits
                    </Button>
                  </div>
                  <div>
                    <Label htmlFor="credits-amount" className="text-sm font-medium">Amount</Label>
                    <Input
                      id="credits-amount"
                      type="number"
                      min="1"
                      placeholder="Enter amount..."
                      value={creditsDialog.amount}
                      onChange={(e) => setCreditsDialog(prev => ({ ...prev, amount: e.target.value }))}
                      className="mt-1"
                      data-testid="credits-amount-input"
                    />
                  </div>
                  {creditsDialog.amount && parseInt(creditsDialog.amount) > 0 && (
                    <p className="text-sm text-muted-foreground">
                      New balance will be: <strong className="text-foreground">
                        {((creditsDialog.user?.credits || 0) + (creditsDialog.type === 'subtract' ? -parseInt(creditsDialog.amount) : parseInt(creditsDialog.amount))).toLocaleString()} credits
                      </strong>
                    </p>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updatingCredits}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCreditsSubmit}
              disabled={updatingCredits || !creditsDialog.amount || parseInt(creditsDialog.amount) <= 0}
              className={creditsDialog.type === 'subtract' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
              data-testid="confirm-credits-btn"
            >
              {updatingCredits ? 'Processing...' : creditsDialog.type === 'subtract' ? 'Subtract' : 'Add'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
